<template>
    <div id="app">
        
        <!-- Loading Spinner -->
        <div class="loading-container" v-show="loading">
            <div class="loading mx-auto">
                <div class="custom-spinner" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>

        <top-nav class="position-relative" ref="topnav"></top-nav>

        <!-- Main View Container -->
        <div>
            <router-view></router-view>
            <div v-show="!caseSessionActive">
                <br />
                <br />
            </div>
        </div>

        <!-- Vue Notifications -->
        <notifications group="main" :max="3" :reverse="false" :duration="2000" position="bottom right" class="mt-3">
            <template slot="body" slot-scope="props">
                <div class="ml-3 mr-3 mb-2 border rounded notification-toast shadow-sm" role="alert">
                    <div>
                        <strong v-show="props.item.title">{{ props.item.title }}</strong>
                        <button @click="props.close" type="button" class="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <span
                        class="icon icon-glyphicons-basic-circle-check text-success mr-1"
                        v-if="props.item.type === 'success'"
                    ></span>
                    <span
                        class="icon icon-glyphicons-basic-circle-alert text-danger mr-1"
                        v-if="props.item.type === 'danger'"
                    ></span>
                    <span
                        class="icon icon-glyphicons-basic-circle-alert text-primary mr-1"
                        v-if="props.item.type === 'primary'"
                    ></span>
                    <span class="align-middle ml-1">{{ props.item.text }}</span>
                    <span v-if="props.item.data.link">
                        <router-link :to="props.item.data.link" class="alert-link">
                            {{ props.item.data.linkText }}
                        </router-link>
                        <span>.</span>
                    </span>
                </div>
            </template>
        </notifications>
    </div>
</template>

<script>
import "bootstrap"
import TopNav from "./components/TopNav"
import http from "@/axios.js"
import throttle from "lodash.throttle"

export default {
    name: "app",
    components: {
        TopNav
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        caseSessionActive() {
            return [
                "PreviewCaseSession",
                "LearnerCaseSession",
                "CommunityCaseSession",
                "ShareLinkCaseSession",
                "PageViewerSession",
                "PageViewerShareSession",
            ].includes(this.$route.name)
        },
    },
    methods: {
        // show an error notification
        showError: throttle(function() {
            this.$notify({
                group: "main",
                type: "danger",
                text: "Something went wrong. Please try again later.",
                ignoreDuplicates: true,
                duration: 6000,
                data: {}
            })
        }, 1000),
        createRootRedirect(toURL) {
            this.$router.addRoute({
                path: "/",
                redirect: toURL
            })
        }
    },
    beforeCreate() {
        this.$store.dispatch("addLoad")
        let vm = this
        // Check authentication and authorization.
        this.$store.dispatch("fetchAuth").then(function(response) {
            let user = response.data
            // Create root url redirect based on the user's role (if the user has a membership & role).
            if (user.selected_membership) {
                let isAuthorOrAdmin = ["admin", "author"].includes(user.selected_membership.role)

                let redirectURL = "/learner"
                if (isAuthorOrAdmin) {
                    vm.$store.dispatch("fetchOrganizationDetails")
                    redirectURL = "/library"
                }
                vm.createRootRedirect(redirectURL)

                if (isAuthorOrAdmin) {
                    // Show the topnav welcome tour if the associated query parameter is set, or if
                    // the user hasn't been shown it yet.
                    if (!user.viewed_tours.author_nav || vm.$route.query.showTour) {
                        vm.$refs.topnav.openWelcomeTourModal()
                        if (vm.$route.query.showTour) {
                            // Clear the query parameter.
                            vm.$router.replace({ query: null })
                        }
                        // Track that the user has been shown the tour, to avoid showing it twice.
                        http.put("/api/tour-viewed/", { tourId: "author_nav" })
                    // Show the privacy notice update tour.
                    } else if (!user.viewed_tours.privacy_update_2025_06 && !user.exclude_from_tours.privacy_update_2025_06) {
                        vm.$refs.topnav.openPrivacyNoticeModal()
                        // Track that the user has been shown the notification to avoid showing it twice.
                        vm.$store.dispatch("setTourViewed", "privacy_update_2025_06")
                    }
                }
            } else {
                if (user.default_view == "history") {
                    vm.createRootRedirect("/history/")
                } else {
                    vm.createRootRedirect("/community/browse")
                }
            }
        }).finally(() => {
            vm.$store.dispatch("removeLoad")
        })
    },
    created() {
        let vm = this
        // Set up interceptor for global error handling.
        http.interceptors.response.use(
            function(response) {
                return response
            },
            function(error) {
                // Ignore canceled requests (don’t show global error)
                if (error?.code === 'ERR_CANCELED') {
                    return Promise.reject(error)
                }

                const status = error?.response?.status
                const url = error?.config?.url || ''
                // Hack - exclude errors encountered while prefetching elements, as this functionality
                // occasionally produces errors. Don't show the user, as this isn't a blocking error.
                const excludedUrl = url.includes('/prefetch-elements')

                // No HTTP response (network/CORS/timeout), show and bail early.
                if (status == null) {
                    vm.showError()
                    vm.$store.dispatch("removeLoad")
                    return Promise.reject(error)
                }

                // Don't show global error message for unauthorized errors, as these are handled
                // by a redirect. Also skip showing error for 503 Service Unavailable, as these
                // have been observed to be intermittently thrown while pre-loading static files
                // in learner views.
                if (![401, 403, 503].includes(status) && !excludedUrl) {
                    vm.showError()
                }

                vm.$store.dispatch("removeLoad")
                return Promise.reject(error)
            }
        )
    }
}
</script>

<style lang="scss">
@import "@/assets/styles/main.scss";
@import "@/assets/styles/variables.scss";

$background-base: #fafbfd;

html {
    height: 100%;
    background-color: $background-base;
}

body {
    height: 100%;
}

.background-case-session {
    background-color: $background-base !important;
}

#app {
    min-height: 100vh;
    background-color: $background-base;
}

.loading-container {
    position: fixed;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    text-align: center;
    background-color: #ffffff7a;
    z-index: 100;
}

.loading {
    display: inline-block;
}

.notifications-container {
    margin-bottom: 60px;
}

.notification-toast {
    background-color: rgba(255, 255, 255, 0.97);
    padding: 1.1rem 1rem;
    // backdrop-filter: blur(10px);

    .glyphicons {
        font-size: 22px;
        padding-right: 2px;
    }
}
</style>